/* eslint-disable no-param-reassign */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import {
  getAccessToken,
  redirectToLogin,
  renewUser,
} from 'src/shared/services/identity/sessionService';

declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface AxiosResponse<T = unknown> extends Promise<T> {}
  interface AxiosRequestConfig {
    retry?: boolean;
  }
}

// const getAppLanguage = (): string => {
//   return 'SE';
// };

// AXIOS CREATE INSTANCE
const AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// AXIOS PARAMS
AxiosInstance.defaults.paramsSerializer = (params) => {
  // ConsoleHelper.log('paramsSerializer', params);
  const formatted = qs.stringify(params);
  return formatted;
};

// AXIOS - Destruct data from response - returnerar res.data
const handleResponse = ({ data }: AxiosResponse) => {
  return data;
};
AxiosInstance.interceptors.response.use(handleResponse);

// AXIOS REQUEST
AxiosInstance.interceptors.request.use(
  async (config) => {
    // ConsoleHelper.log('intercetor request: ', config);
    const token = getAccessToken();
    // config.headers['X-Language'] = getAppLanguage() ?? 'EN';
    // config.headers['X-Version'] = '2.0.0|10146|2021-02-26';
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// AXIOS RESPONSE QUEUE
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error || !token) {
      prom.reject(error ?? new Error('No token returned from silent renew.'));
      return;
    }

    prom.resolve(token);
  });
  failedQueue = [];
};

// AXIOS RESPONSE
AxiosInstance.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config as AxiosRequestConfig | undefined;

    if (
      err?.response?.status === 401 &&
      originalRequest &&
      !originalRequest.retry
    ) {
      ConsoleHelper.warn('[Auth] 401 detected for:', originalRequest.url);

      if (isRefreshing) {
        ConsoleHelper.log(
          '[Auth] Silent renew already in progress, queuing request',
        );
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return AxiosInstance(originalRequest);
          })
          .catch((queueErr) => Promise.reject(queueErr));
      }

      originalRequest.retry = true;
      isRefreshing = true;

      try {
        ConsoleHelper.log('[Auth] Attempting signinSilent to refresh token');
        const refreshedUser = await renewUser();
        const newToken = refreshedUser.access_token;
        ConsoleHelper.log(
          '[Auth] signinSilent succeeded, retrying original request',
        );
        processQueue(null, newToken);
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return AxiosInstance(originalRequest);
      } catch (silentErr) {
        ConsoleHelper.warn(
          '[Auth] signinSilent failed, redirecting to login:',
          silentErr,
        );
        processQueue(silentErr, null);
        await redirectToLogin();
        return Promise.reject(err.response ?? silentErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err.response ?? err);
  },
);

export default {
  get: AxiosInstance.get,
  post: AxiosInstance.post,
  put: AxiosInstance.put,
  delete: AxiosInstance.delete,
  patch: AxiosInstance.patch,
};
